import { Conversation } from '@elevenlabs/client';
import { toast } from 'sonner';
import { AgentConfig, PitchType, SparTranscriptEntry, VoiceAgentInterface } from './types';

/**
 * ElevenLabs voice-roleplay agent for focused-view Spar (practice) sessions.
 *
 * Forked from the legacy `voice-agent/services/VoiceAgent.ts`, but trimmed to the
 * ElevenLabs (Conversational AI) provider only. The legacy class also carried an
 * OpenAI Realtime path; this product does not run OpenAI voice models (legacy
 * `VoiceAgentModal` pins `provider: 'elevenlabs'`), so that path was dead weight
 * in the fork and has been removed. The agent owns the live ElevenLabs
 * `Conversation` (hot mic + WebSocket/WebRTC) and pushes finalized transcript
 * lines through `onNewTranscriptEntry`.
 */
export class VoiceAgent implements VoiceAgentInterface {
	private apiKey: string;
	private transcript: SparTranscriptEntry[] = [];
	private onTranscriptUpdate?: (transcript: SparTranscriptEntry[]) => void;
	private onNewTranscriptEntry?: (entry: SparTranscriptEntry) => void;
	private conversation: Conversation | null = null;
	private conversationId: string | null = null;

	constructor(config: AgentConfig) {
		this.apiKey = config.apiKey;
		this.onTranscriptUpdate = config.onTranscriptUpdate;
		this.onNewTranscriptEntry = config.onNewTranscriptEntry;
	}

	async connect(pitchType: PitchType, customPrompt?: string): Promise<void> {
		// Re-entrancy backstop: a second connect() without an intervening disconnect
		// would start a fresh ElevenLabs Conversation and orphan the previous one's
		// hot mic + socket. useSparSession guards double-start today, but the class
		// shouldn't depend on its caller for that invariant.
		if (this.conversation) {
			this.disconnect();
		}

		// Detect connection type from the token format:
		// - Signed URLs start with "wss://" -> WebSocket
		// - Conversation tokens don't -> WebRTC
		const isSignedUrl = this.apiKey.startsWith('wss://');

		const options = {
			// Dynamic auth: signedUrl for WebSocket, conversationToken for WebRTC.
			...(isSignedUrl ? { signedUrl: this.apiKey } : { conversationToken: this.apiKey }),
			overrides: {
				agent: {
					prompt: {
						prompt: customPrompt || `You are a sales prospect. Act as a potential customer for a ${pitchType} call.`,
					},
					// Blank first message so the sales rep initiates the conversation.
					firstMessage: '',
				},
			},
			onConnect: ({ conversationId }: { conversationId: string }) => {
				this.conversationId = conversationId;
				toast.success('Connected to practice partner');
			},
			onMessage: ({ message, source }: { message: string; source: string }) => {
				const transcriptEntry: SparTranscriptEntry = {
					speaker: source === 'user' ? 'Sales Rep' : 'Prospect',
					message: message || '',
					timestamp: new Date(),
				};

				this.transcript.push(transcriptEntry);
				this.onTranscriptUpdate?.([...this.transcript]);
				this.onNewTranscriptEntry?.(transcriptEntry);
			},
		};

		// Type assertion needed due to the discriminated union in the SDK's option types.
		this.conversation = await Conversation.startSession(options as Parameters<typeof Conversation.startSession>[0]);
	}

	getTranscript(): SparTranscriptEntry[] {
		return [...this.transcript];
	}

	getFormattedTranscript(): string {
		return this.transcript
			.map(entry => {
				const time = entry.timestamp.toLocaleTimeString();
				return `[${time}] ${entry.speaker}: ${entry.message}`;
			})
			.join('\n');
	}

	clearTranscript(): void {
		this.transcript = [];
		this.onTranscriptUpdate?.([]);
	}

	getConversationId(): string | null {
		return this.conversationId;
	}

	disconnect(): void {
		if (this.conversation) {
			this.conversation.endSession();
			this.conversation = null;
		}
		this.conversationId = null;
	}
}
